import { useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

interface SiteSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const navLinks = [
  { label: "Home", path: "/", icon: "ri-home-line" },
  { label: "Pricing", path: "/pricing", icon: "ri-price-tag-3-line" },
  { label: "Monthly Challenge", path: "/monthly-challenge", icon: "ri-trophy-line" },
  { label: "Leaderboard", path: "/reseller-leaderboard", icon: "ri-bar-chart-line" },
  { label: "Buy Data", path: "/buy", icon: "ri-shopping-cart-line" },
  { label: "Track Order", path: "/track-order", icon: "ri-truck-line" },
  { label: "API Docs", path: "/api-docs", icon: "ri-code-box-line" },
];

export default function SiteSidebar({ isOpen, onClose }: SiteSidebarProps) {
  const location = useLocation();
  const { isLoggedIn, user, logout } = useAuth();
  const sidebarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("touchstart", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("touchstart", handleClick);
    };
  }, [isOpen, onClose]);

  const isActive = (path: string) => location.pathname === path;

  const handleNavClick = () => {
    onClose();
  };

  const name = user?.name || "U";
  const gradients = [
    "from-rose-400 to-orange-300",
    "from-accent-400 to-pink-400",
    "from-primary-400 to-cyan-400",
    "from-violet-400 to-fuchsia-300",
    "from-sky-400 to-indigo-400",
    "from-teal-400 to-lime-300",
  ];
  const gradientIndex = name.charCodeAt(0) % gradients.length;
  const gradient = gradients[gradientIndex];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[55] animate-[fadeIn_0.2s_ease-out]">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>
      <div
        ref={sidebarRef}
        className="absolute left-0 top-0 h-full w-[min(85vw,20rem)] bg-white border-r border-foreground-100 flex flex-col animate-[slideInLeft_0.3s_ease-out] shadow-2xl"
      >
        {/* Header */}
        <div className="px-5 py-5 border-b border-foreground-100 flex items-center justify-between">
          <Link to="/" onClick={handleNavClick} className="flex items-center gap-2">
            <img
              src="https://static.readdy.ai/image/4371883fa286ac0088227d507facd13a/cee86cc6ab83fcd7b536d5a6d3288f01.png"
              alt="Man Aya DataLink Logo"
              className="h-9 w-auto object-contain"
            />
          </Link>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-foreground-100 hover:bg-foreground-200 cursor-pointer transition-colors"
            aria-label="Close sidebar"
          >
            <i className="ri-close-line text-foreground-600 text-base"></i>
          </button>
        </div>

        {/* User Section */}
        {isLoggedIn && (
          <div className="px-5 py-4 border-b border-foreground-100">
            <div className="flex items-center gap-3">
              {user?.avatarUrl ? (
                <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 ring-[2.5px] ring-accent-400 ring-offset-2 ring-offset-white">
                  <img
                    src={user.avatarUrl}
                    alt={name}
                    className="w-full h-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                </div>
              ) : (
                <span className={`w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-br ${gradient} text-white text-sm font-bold flex-shrink-0`}>
                  {name.charAt(0).toUpperCase()}
                </span>
              )}
              <div className="min-w-0">
                <p className="text-sm font-bold text-foreground-900 truncate">{name}</p>
                <p className="text-xs text-foreground-400 truncate">{user?.email || ""}</p>
              </div>
            </div>
            {user?.role === "reseller" && (
              <Link
                to="/wallet"
                onClick={handleNavClick}
                className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-accent-50 border border-accent-200 hover:bg-accent-100 cursor-pointer transition-colors"
              >
                <i className="ri-wallet-3-line text-accent-600 text-base w-5 h-5 flex items-center justify-center"></i>
                <span className="text-xs font-semibold text-accent-700">Wallet</span>
                <span className="ml-auto text-xs font-bold text-primary-600 bg-primary-50 px-2 py-0.5 rounded-full">
                  {user?.cashbackBalance != null
                    ? `GH₵${user.cashbackBalance.toFixed(2)}`
                    : "GH₵0.00"}
                </span>
              </Link>
            )}
          </div>
        )}

        {/* Nav Links */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <p className="px-4 text-[11px] font-bold text-foreground-400 uppercase tracking-wider mb-2">
            Navigation
          </p>
          <div className="space-y-1">
            {/* Dashboard link — always visible for logged-in users */}
            {isLoggedIn && (
              <Link
                key="/dashboard"
                to="/dashboard"
                onClick={handleNavClick}
                className={`relative w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-colors cursor-pointer whitespace-nowrap ${
                  isActive("/dashboard")
                    ? "bg-accent-500 text-white shadow-md shadow-accent-500/25"
                    : "text-foreground-700 hover:bg-foreground-100 hover:text-foreground-900"
                }`}
              >
                {isActive("/dashboard") && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-full bg-yellow-400"></span>
                )}
                <i className="ri-dashboard-line text-lg w-6 h-6 flex items-center justify-center"></i>
                <span className="flex-1 text-left">My Dashboard</span>
              </Link>
            )}
            {navLinks.map((link) => {
              const active = isActive(link.path);
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={handleNavClick}
                  className={`relative w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-colors cursor-pointer whitespace-nowrap ${
                    active
                      ? "bg-accent-500 text-white shadow-md shadow-accent-500/25"
                      : "text-foreground-700 hover:bg-foreground-100 hover:text-foreground-900"
                  }`}
                >
                  {active && (
                    <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-full bg-yellow-400"></span>
                  )}
                  <i className={`${link.icon} text-lg w-6 h-6 flex items-center justify-center`}></i>
                  <span className="flex-1 text-left">{link.label}</span>
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Bottom Actions */}
        <div className="px-4 py-4 border-t border-foreground-100 space-y-2">
          {isLoggedIn ? (
            <>
              <Link
                to="/profile"
                onClick={handleNavClick}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-600 hover:bg-foreground-100 cursor-pointer transition-colors"
              >
                <i className="ri-user-line text-lg w-6 h-6 flex items-center justify-center"></i>
                My Account
              </Link>
              {user?.role === "reseller" && (
                <Link
                  to="/dashboard"
                  onClick={handleNavClick}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-600 hover:bg-foreground-100 cursor-pointer transition-colors"
                >
                  <i className="ri-dashboard-line text-lg w-6 h-6 flex items-center justify-center"></i>
                  Reseller Dashboard
                </Link>
              )}
              {user?.role === "reseller" && (
                <Link
                  to="/wallet"
                  onClick={handleNavClick}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-600 hover:bg-foreground-100 cursor-pointer transition-colors"
                >
                  <i className="ri-wallet-3-line text-lg w-6 h-6 flex items-center justify-center"></i>
                  Wallet
                  {user?.cashbackBalance != null && (
                    <span className="ml-auto text-xs font-bold text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded">
                      GH₵{user.cashbackBalance.toFixed(2)}
                    </span>
                  )}
                </Link>
              )}
              <button
                onClick={() => { logout(); onClose(); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 cursor-pointer transition-colors"
              >
                <i className="ri-logout-box-r-line text-lg w-6 h-6 flex items-center justify-center"></i>
                Sign Out
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                onClick={handleNavClick}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-700 hover:bg-foreground-100 cursor-pointer transition-colors"
              >
                <i className="ri-login-box-line text-lg w-6 h-6 flex items-center justify-center"></i>
                Sign In
              </Link>
              <Link
                to="/register"
                onClick={handleNavClick}
                className="w-full flex items-center justify-center gap-2 bg-accent-500 hover:bg-accent-600 text-white font-bold text-sm py-3 rounded-xl cursor-pointer transition-colors"
              >
                <i className="ri-user-add-line"></i>
                Create Account
              </Link>
              <Link
                to="/reseller"
                onClick={handleNavClick}
                className="w-full flex items-center justify-center gap-2 border border-primary-300 text-primary-700 hover:bg-primary-50 font-bold text-sm py-3 rounded-xl cursor-pointer transition-colors"
              >
                <i className="ri-store-3-line"></i>
                Become a Reseller
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}